import { FormProvider, useForm } from 'react-hook-form'
import { Button } from '../../shared/ui/button'
import { MainFields } from './ui/mainFields'
import { IntegrationSelector } from './ui/integrationSelector'
import { useFormStyles } from '../../shared/lib/hooks/useFormStyles'
import { observer } from 'mobx-react-lite'
import { useApi, useStore } from '../../../hooks/storeHook'
import { TExistingFile, TFormValues } from './productForm.types'
import { mapFormValuesToFormData } from './api/mapper'
import { normalizeDescriptionHtml } from '../../shared/lib/htmlDescriptionUtility'
import { useMessages } from '../../../hooks/snackbarHooks'
import { useHistory } from 'react-router-dom'
import { makeMpCardAccountUrl } from '../../../common/mpCardRoutes'
import { required } from '../../../common/objectUtils'
import { MarketplacesFields } from './ui/marketplacesFields'
import { IntegrationType } from '../../../server/mpsklad_core/Entity/IntegrationType'
import { ImageDropzoneList } from './ui/imageDropzoneList'
import { useBoolState, useLazyEffect } from '../../../hooks/commonHooks'
import { AttributeLoadingProvider } from './lib/attributeLoadingContext'
import { getAttributeDiffMap } from './lib/getAttributeDiffMap'
import { AttributesDiffContext } from './lib/attributeDiffContext'
import { WbVariantsContainer } from './ui/wb/wbVariantsContainer'

export const ProductForm = observer(() => {
  const [isSubmitting, setSubmitting, setFinished] = useBoolState()

  const {productStore: {selectedProductCard, marketplaceProductCard, clearProductCards}, homeNavRequired} = useStore()

  const {showSuccess, showError} = useMessages()

  const history = useHistory()

  const api = useApi()

  const methods = useForm<TFormValues>({
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues:
        {
          attributes: [],
          description: null,
          images: [],
          ozon: {
            include: false,
            category: undefined,
            attributes: []
          },
          wildberries: {
            include: false,
            category: undefined,
            attributes: []
          },
          yandexMarket: {
            include: false,
            category: undefined,
            attributes: []
          },
          moySklad: {
            include: false,
            category: undefined,
            attributes: []
          }
        }
    }
  )

  useLazyEffect(() => {
    if (selectedProductCard) {
      const descriptionAttr = selectedProductCard.attributes.find(a => a.name === 'Описание')

      methods.reset({
        attributes: selectedProductCard.attributes.filter(a => a.name && a.name !== 'Описание' && !a.integrationType).map(
          attr => (attr)),
        description: descriptionAttr
                   ? {...descriptionAttr, currentValue: normalizeDescriptionHtml(descriptionAttr.currentValue)}
                   : null,
        ozon: {
          include: !!selectedProductCard.ozonCategory?.id,
          category: selectedProductCard.ozonCategory,
          attributes: selectedProductCard.attributes.filter(a => a.integrationType === IntegrationType.Ozon).map(
            attr => (attr))
        },
        wildberries: {
          include: !!selectedProductCard.wbCategory?.id,
          category: selectedProductCard.wbCategory,
          attributes: selectedProductCard.attributes.filter(a => a.integrationType === IntegrationType.Wildberries).map(
            attr => (attr))
        },
        yandexMarket: {
          include: !!selectedProductCard.yandexMarketCategory?.id,
          category: selectedProductCard.yandexMarketCategory,
          attributes: selectedProductCard.attributes.filter(
            a => a.integrationType === IntegrationType.YandexMarket).map(
            attr => (attr))
        },
        moySklad: {
          include: !!selectedProductCard.moySkladCategory?.id,
          category: selectedProductCard.moySkladCategory,
          attributes: selectedProductCard.attributes.filter(a => a.integrationType === IntegrationType.MoySklad).map(
            attr => (attr))
        },
        images: selectedProductCard.images
      })
    }
  }, [selectedProductCard])

  const originalAttrs = marketplaceProductCard?.attributes ?? []
  const modifiedAttrs = selectedProductCard?.attributes ?? []
  const diffMap = getAttributeDiffMap(originalAttrs, modifiedAttrs)

  const classes = useFormStyles()

  const onSubmit = async (data: TFormValues) => {
    setSubmitting()

    try {
      let deletedImageIds: string[] = []

      if (selectedProductCard?.id) {
        const originalImageIds = selectedProductCard.images
                                                    .filter((img): img is TExistingFile => 'id' in img)
                                                    .map(img => img.id)

        const currentImageIds = data.images
                                    .filter((img): img is TExistingFile => 'id' in img)
                                    .map(img => img.id)

        deletedImageIds = originalImageIds.filter(id => !currentImageIds.includes(id))
      }

      const formData = mapFormValuesToFormData(data, {
        deletedImageIds,
        productId: selectedProductCard?.id
      })

      if (!selectedProductCard?.id) {
        await api.createProductCard(formData)
      } else {
        await api.updateProductCard(formData)
      }

      showSuccess('Карточка успешно сохранена')

      clearProductCards()
      history.push(makeMpCardAccountUrl(
        homeNavRequired.integrationType,
        required(homeNavRequired.accountId)))
    } catch (e) {
      throw e
    } finally {
      setFinished()
    }
  }

  const onError = () => {
    showError('Проверьте правильность заполнения формы')
  }

  const includeWb = methods.watch('wildberries.include');

  return <FormProvider {...methods}>
    <AttributeLoadingProvider>
      <AttributesDiffContext.Provider value={diffMap}>
        <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
          <ImageDropzoneList/>
          <IntegrationSelector/>
          <MainFields showGenerateButton={includeWb} />
          <MarketplacesFields />
          {includeWb && selectedProductCard && (
            <WbVariantsContainer
              existingVariants={selectedProductCard.variants || []}
              editableAttributes={selectedProductCard.attributes.filter(
                a => a.integrationType === IntegrationType.Wildberries && a.name !== 'brand'
              )}
              parentCardId={selectedProductCard?.id }
            />
          )}
          <Button
            className={classes.button}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Сохраняем...' : 'Сохранить карточку'}
          </Button>
        </form>
      </AttributesDiffContext.Provider>
    </AttributeLoadingProvider>
  </FormProvider>
})